import type { ObjectShape } from 'yup/lib/object';
import * as Yup from 'yup';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as ibantools from 'ibantools';

const nameWithOnlyLatinLettersRegex =
  /^([A-Za-z\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*)$/gi;

export const useSetupMangopayDirectDebitValidationSchema = (): ObjectShape => {
  const { t } = useTranslation();

  return useMemo(
    () => ({
      firstName: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.firstName'))
        .matches(
          nameWithOnlyLatinLettersRegex,
          t('mangopay.direct-debit.setup.valid.firstName')
        ),
      lastName: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.lastName'))
        .matches(
          nameWithOnlyLatinLettersRegex,
          t('mangopay.direct-debit.setup.valid.lastName')
        ),
      iban: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.iban'))
        .test({
          name: 'check-iban',
          test: (rawIban, context) => {
            const iban = rawIban ?? '';
            const isValidIban =
              ibantools.isValidIBAN(iban) ||
              ibantools.isValidIBAN(ibantools.electronicFormatIBAN(iban) ?? '');
            return (
              isValidIban ||
              context.createError({
                message: t('mangopay.direct-debit.setup.valid.iban'),
              })
            );
          },
        })
        .test({
          name: 'check-iban-country',
          test: (rawIban, context) => {
            const iban = rawIban ?? '';
            const country = context.parent.country;

            // Skip validation if IBAN or country is empty
            if (!iban || !country) {
              return true;
            }

            // Extract country code from IBAN (first 2 characters)
            const electronicIban = ibantools.electronicFormatIBAN(iban) ?? iban;
            const extractResult = ibantools.extractIBAN(electronicIban);
            const ibanCountry = extractResult.countryCode;

            // Skip validation if we couldn't extract a valid country code
            if (!ibanCountry) {
              return true;
            }

            // Check if IBAN country matches selected country
            if (ibanCountry.toUpperCase() !== country.toUpperCase()) {
              return context.createError({
                message: t(
                  'mangopay.recipient.errors.IBAN_DOES_NOT_CORRESPOND_TO_ACCOUNT_COUNTRY'
                ),
              });
            }

            return true;
          },
        }),
      addressLine1: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.address-line-1'))
        .test({
          name: 'no-slash',
          test: (value, context) => {
            if (value && value.includes('/')) {
              return context.createError({
                message: t('mangopay.direct-debit.setup.invalid.address-slash'),
              });
            }
            return true;
          },
        }),
      addressLine2: Yup.string().test({
        name: 'no-slash',
        test: (value, context) => {
          if (value && value.includes('/')) {
            return context.createError({
              message: t('mangopay.direct-debit.setup.invalid.address-slash'),
            });
          }
          return true;
        },
      }),
      city: Yup.string().required(
        t('mangopay.direct-debit.setup.required.city')
      ),
      country: Yup.string().required(
        t('mangopay.direct-debit.setup.required.country')
      ),
      postalCode: Yup.string()
        .required(t('mangopay.direct-debit.setup.required.postal-code'))
        .matches(/^\d+$/, t('mangopay.direct-debit.setup.valid.postal-code')),
    }),
    [t]
  );
};
